import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../services/authentication.service';

/**
 * Errori JavaScript non gestiti: oltre alla console, vengono inviati a POST /api/client-logs, che li scrive nel
 * log applicativo (canale JSON → Promtail → Loki), accanto alle righe del backend dello stesso utente e minuto.
 * Gli errori HTTP (HttpErrorResponse) non passano di qui: li gestisce error.interceptor e il backend li ha già
 * loggati. Limiti: lo stesso errore una volta sola per sessione di pagina, al massimo 20 invii al minuto, solo con
 * utente autenticato (l'endpoint richiede il token). Un errore nell'invio non viene mai rilanciato.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
	private static readonly MAX_PER_MINUTE = 20;

	private readonly sent = new Set<string>();
	private windowStart = Date.now();
	private sentInWindow = 0;

	constructor(private readonly _injector: Injector) {}

	handleError(error: unknown): void {
		console.error(error);

		try {
			this.report(error);
		} catch {
			// Il logger non deve mai essere causa di un secondo errore
		}
	}

	private report(error: unknown): void {
		const unwrapped = (error as { rejection?: unknown })?.rejection ?? error; // promise non gestite
		if (unwrapped instanceof HttpErrorResponse) {
			return;
		}

		const auth = this._injector.get(AuthenticationService, null);
		if (!auth?.currentUserValue) {
			return;
		}

		const message = unwrapped instanceof Error ? `${unwrapped.name}: ${unwrapped.message}` : String(unwrapped);
		const stack = unwrapped instanceof Error ? (unwrapped.stack ?? '').slice(0, 8000) : undefined;
		const key = `${message}|${window.location.pathname}`;
		if (this.sent.has(key) || !this.allowOneMore()) {
			return;
		}
		this.sent.add(key);

		const env = (window as unknown as { env?: { appVersion?: string } }).env;
		this._injector
			.get(HttpClient)
			.post(`${environment.apiUrl}/client-logs`, {
				level: 'error',
				message: message.slice(0, 2000),
				stack,
				url: window.location.pathname + window.location.search,
				user_agent: navigator.userAgent.slice(0, 500),
				build: env?.appVersion ?? 'dev',
			})
			.subscribe({ next: () => undefined, error: () => undefined });
	}

	private allowOneMore(): boolean {
		const now = Date.now();
		if (now - this.windowStart > 60_000) {
			this.windowStart = now;
			this.sentInWindow = 0;
		}

		return ++this.sentInWindow <= GlobalErrorHandler.MAX_PER_MINUTE;
	}
}
